import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, LogIn, FileDown, Mail, UserPlus, UserX, Activity } from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  "board.login": { icon: LogIn, label: "Signed in", tone: "text-emerald-300" },
  "board.finance_export": { icon: FileDown, label: "Exported finance CSV", tone: "text-orange-300" },
  "board.packet.send": { icon: Mail, label: "Sent board packet", tone: "text-indigo-300" },
  "board.invite": { icon: UserPlus, label: "Invited director", tone: "text-emerald-300" },
  "board.resend": { icon: Mail, label: "Resent invite", tone: "text-zinc-300" },
  "board.revoke": { icon: UserX, label: "Revoked director", tone: "text-red-300" },
  "board.update": { icon: UserPlus, label: "Updated director", tone: "text-zinc-300" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

/**
 * BoardActivityCard — feed of every board interaction (logins, CSV exports,
 * packet emails, invites, revokes). Backed by /api/admin/board/activity.
 * Compact prop = true renders a slimmer version suited for the Finance page.
 */
export function BoardActivityCard({ compact = false, limit = 25 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/board/activity", { params: { limit } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className={"border-white/10 bg-card " + (compact ? "p-3" : "p-5")} data-testid="board-activity-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="overline">Board activity</div>
          <div className="text-base font-semibold font-display mt-0.5 inline-flex items-center gap-2">
            <Activity className="h-3.5 w-3.5 text-indigo-400" /> Recent director interactions
          </div>
          {!compact && (
            <div className="text-xs text-zinc-500 mt-1">
              Every login, CSV export, and packet email is logged so property managers can trace board access.
            </div>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-7 px-2" data-testid="board-activity-refresh">
          <RefreshCw className={"h-3 w-3 " + (loading ? "animate-spin" : "")} />
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 p-6 text-center text-xs text-zinc-500" data-testid="board-activity-empty">
          No board activity yet. Invite a director to get started.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {items.map((e) => {
            const meta = ACTION_META[e.action] || { icon: Activity, label: e.action, tone: "text-zinc-300" };
            const Icon = meta.icon;
            return (
              <div key={e.id || `${e.seq}-${e.at}`}
                   className="flex items-start gap-2 rounded-md border border-white/5 px-2.5 py-2 text-xs"
                   data-testid={`board-activity-row-${e.action}`}>
                <div className={"mt-0.5 h-6 w-6 shrink-0 rounded-md border border-white/10 flex items-center justify-center " + meta.tone}>
                  <Icon className="h-3 w-3" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-200 truncate">
                    <span className="font-medium">{e.actor_name || "Unknown"}</span>
                    <span className="text-zinc-500 mx-1">·</span>
                    <span>{meta.label}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {timeAgo(e.at)} · {e.actor_role} · {e.resource || ""}{e.resource_id && e.resource_id !== "-" ? ` (${e.resource_id})` : ""}
                  </div>
                </div>
                <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px] uppercase tracking-widest">
                  {e.actor_role}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default BoardActivityCard;
